import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Stack, IconButton, Box, Tooltip } from "@mui/material";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { ArrowLeftOutlined, ArrowRightOutlined, Check } from '@mui/icons-material';
import { useLSContext } from '../Context/useLSContext';
import { useDataContext } from '../Context/useDataContext';
import { useHoleSettings } from '../Context/useHoleSettingsContext';
import CreateApi from '../Settings/appSettings';
import { RoundCompleteFollowUp } from './RoundCompleteFollowUp';
import { EndOfRoundPopup } from './EndOfRoundPopup';
import { ChallengeSubmissionPrompt } from './ChallengeSubmissionPrompt';
import { ChallengeAcknowledgePrompt } from './ChallengeAcknowledgePrompt';
import { saveLastHole } from '../utils/lastHoleStorage';
import { usePendingScoreContext } from '../Context/PendingScoreContext';
import { buildHoleEnqueueItems, isScoreCheckComplete } from '../utils/enqueueHoleScoreUpdates';
import { holeChallengeStatus } from '../utils/holeChallengeStatus';
import {
    hasAcknowledgedChallenge,
    markChallengeAcknowledged,
} from '../utils/challengeAcknowledgeStorage';
import { usePaceOfPlay } from '../Hooks/usePaceOfPlay';
import { AcknowledgedMessageStrip } from './AcknowledgedMessageStrip';
import { useMessageContext } from '../Context/useMessageContext';
import { hasActivePacePushMessage } from '@moment-golf/types';

export const NavigationBar = () => {

    const api = CreateApi();
    
    const {
        roundReview, scoreLock, toggleScoring, setRoundReview, setScoreViewTabs,
        factor, setCurrentHole, setToolsOpen, setToolsTab,
    } = useLSContext();
    const { scoreData, eventData, courseData, currentRound, startHole, scoreCode, scoreDataRef } = useDataContext();
    const { holeSettings, setHoleSettings } = useHoleSettings();
    const { enqueue, flushQueue } = usePendingScoreContext();
    const paceDisplay = usePaceOfPlay();
    const { activeMessages } = useMessageContext();
    const showPaceDisplay = scoreData.length > 0 && hasActivePacePushMessage(activeMessages);
    const roundTotalHoles = useMemo(() => {
        const fromRound = Number(eventData?.rounds?.[currentRound - 1]?.totalHoles);
        const fromEvent = Number(eventData?.totalHoles);
        return (Number.isFinite(fromRound) && fromRound > 0 ? fromRound : 0)
            || (Number.isFinite(fromEvent) && fromEvent > 0 ? fromEvent : 0)
            || 18;
    }, [eventData?.rounds, eventData?.totalHoles, currentRound]);

    const [roundComplete, setRoundComplete] = useState(false);
    const [roundFollowUp, setRoundFollowUp] = useState(false);
    const [scoreCheck, setScoreCheck] = useState<any[]>([]);
    const [endOfRoundOpen, setEndOfRoundOpen] = useState(false);
    const [endOfRoundPhase, setEndOfRoundPhase] = useState<'missing' | 'confirm'>('missing');
    const [challengePromptOpen, setChallengePromptOpen] = useState(false);
    const [pendingHoleDirection, setPendingHoleDirection] = useState<number | null>(null);
    const [pendingChallengeName, setPendingChallengeName] = useState<string | undefined>();
    const [challengeAckOpen, setChallengeAckOpen] = useState(false);
    const [ackChallengeName, setAckChallengeName] = useState<string | undefined>();

    useEffect(() => {
        if (holeSettings.hole !== 0) {
            scoreDataRef.current = JSON.parse(JSON.stringify(scoreData));
            console.log('[LS Nav] scoreDataRef synced on hole change', {
                hole: holeSettings.hole,
                scoreDataRows: scoreData.length,
                refRows: Array.isArray(scoreDataRef.current) ? scoreDataRef.current.length : null,
            });
        }
    }, [holeSettings.hole]);

    useEffect(() => {
        const hole = holeSettings.hole;
        if (!hole || hole <= 0 || scoreData.length === 0 || !eventData || !scoreCode) {
            setChallengeAckOpen(false);
            return;
        }

        if (hasAcknowledgedChallenge(scoreCode, currentRound, hole)) {
            setChallengeAckOpen(false);
            return;
        }

        const { challenge, hasSubmission } = holeChallengeStatus(
            eventData,
            scoreData,
            scoreCode,
            currentRound,
            hole,
        );
        if (!challenge || hasSubmission) {
            setChallengeAckOpen(false);
            return;
        }

        setAckChallengeName(challenge.name);
        setChallengeAckOpen(true);
    }, [holeSettings.hole, eventData, currentRound, scoreCode, scoreData]);

    useEffect(() => {
        if (scoreData.length > 0) {
            const roundCompleteTest = 
            (scoreData.every((score: any) => score.roundScores[currentRound-1].holeScores
            .every((holeScore: any) => holeScore.playerScore !== null && holeScore.playerScore !== 0)) &&
            scoreData[0].roundScores[currentRound-1].markerScoreCode === scoreCode );

            setRoundComplete(roundCompleteTest)
        }
    }, [scoreData, startHole, holeSettings.hole, currentRound, scoreCode])

    const enqueueCurrentHole = useCallback(() => {
        const items = buildHoleEnqueueItems({
            scoreData,
            scoreDataRef,
            holeNumber: holeSettings.hole,
            currentRound,
            scoreCode,
            eventData,
            roundTotalHoles,
        });

        if (items.length > 0) {
            console.log('[LS Nav] enqueuing hole updates', { count: items.length });
            enqueue(items);
        } else {
            console.log('[LS Nav] no changes to enqueue for hole', holeSettings.hole);
        }
    }, [scoreData, currentRound, scoreCode, scoreDataRef, holeSettings.hole, enqueue, eventData, roundTotalHoles]);

    const newHoleSettings = useCallback((newHole: number) => {
        if (eventData && Object.keys(eventData).length > 0 &&
            courseData && Object.keys(courseData).length > 0 &&
            newHole > 0) {

            const playerTeeSet = eventData.playerTeeSet ||
                (courseData.teeSets && courseData.teeSets.length > 0 ? courseData.teeSets[0].teeName : null);

            if (playerTeeSet && courseData.teeSets) {
                const teeSet = courseData.teeSets.find((tee: any) => tee.teeName === playerTeeSet);

                if (teeSet && teeSet.holes && teeSet.holes.length >= newHole) {
                    const teesData = teeSet.holes[newHole - 1];

                    setHoleSettings({
                        hole: newHole,
                        par: teesData.par,
                        dist: teesData.dist,
                        hdcp: teesData.hdcp
                    });

                    setCurrentHole(newHole);
                    saveLastHole(scoreCode, currentRound, newHole);
                    setScoreViewTabs('scorecard');
                }
            }
        }
    }, [
        eventData,
        courseData,
        setHoleSettings,
        setCurrentHole,
        scoreCode,
        currentRound,
        setScoreViewTabs,
    ]);

    const navigateToHole = useCallback((direction: number) => {
        const isMarker = scoreData.every((score: any) =>
            score.roundScores[currentRound - 1].markerScoreCode === scoreCode);

        const newHole = direction === -1
            ? (holeSettings.hole === 1 ? roundTotalHoles : holeSettings.hole - 1)
            : (holeSettings.hole === roundTotalHoles ? 1 : holeSettings.hole + 1);

        console.log('[LS Nav] navigateToHole', {
            direction,
            fromHole: holeSettings.hole,
            toHole: newHole,
            currentRound,
            scoreCode,
            isMarker,
        });

        if (isMarker) {
            enqueueCurrentHole();
            console.log('[LS Nav] background flush triggered — navigating immediately');
            void flushQueue();
        }
        newHoleSettings(newHole);
    }, [
        scoreData,
        currentRound,
        scoreCode,
        holeSettings.hole,
        roundTotalHoles,
        enqueueCurrentHole,
        flushQueue,
        newHoleSettings,
    ]);

    const handleHole = (direction: number) => {
        const isMarker = scoreData.every((score: any) =>
            score.roundScores[currentRound - 1].markerScoreCode === scoreCode);

        if (isMarker) {
            const { challenge, hasSubmission } = holeChallengeStatus(
                eventData,
                scoreData,
                scoreCode,
                currentRound,
                holeSettings.hole,
            );
            if (challenge && !hasSubmission) {
                setPendingHoleDirection(direction);
                setPendingChallengeName(challenge.name);
                setChallengePromptOpen(true);
                return;
            }
        }

        navigateToHole(direction);
    };

    const handleChallengePromptYes = () => {
        setChallengePromptOpen(false);
        setPendingHoleDirection(null);
        setToolsTab('challenges');
        setToolsOpen(true);
    };

    const handleChallengePromptNo = () => {
        const direction = pendingHoleDirection;
        setChallengePromptOpen(false);
        setPendingHoleDirection(null);
        if (direction !== null) {
            navigateToHole(direction);
        }
    };

    const handleChallengeAcknowledge = () => {
        markChallengeAcknowledged(scoreCode, currentRound, holeSettings.hole);
        setChallengeAckOpen(false);
    };

    const handleFinish = async () => {
        saveLastHole(scoreCode, currentRound, holeSettings.hole);

        console.log('[LS Nav] handleFinish — enqueuing current hole and awaiting full flush');
        enqueueCurrentHole();
        await flushQueue();
        console.log('[LS Nav] handleFinish — flush complete, checking scores');

        const scoreCheckResponse = await api.get('/ls/CheckCompleteScores', {
            params: {
                eventID: eventData._id,
                roundNumber: currentRound,
                scoreCode: scoreCode
            }
        });

        const checkData = scoreCheckResponse.data;
        setScoreCheck(checkData);

        if (isScoreCheckComplete(checkData, eventData, scoreCode)) {
            setEndOfRoundPhase('confirm');
        } else {
            setEndOfRoundPhase('missing');
        }
        setEndOfRoundOpen(true);
    }

    const isFinishForward =
        startHole === holeSettings.hole + 1 ||
        (startHole === 1 && holeSettings.hole === roundTotalHoles);

    const forwardDisabled = isFinishForward && !roundComplete;

    const navIconSx = {
        padding: factor * 0.75,
        fontWeight: 'bold' as const,
        backgroundColor: 'primary.light',
        borderRadius: 2 * factor,
        '&:hover': { backgroundColor: 'primary.light' },
        '&:active': { backgroundColor: 'primary.light' },
        '&:disabled': { backgroundColor: 'lightgrey' },
        '&:focus': { backgroundColor: 'primary.light' },
        visibility: scoreLock || toggleScoring ? ('hidden' as const) : ('visible' as const),
        minWidth: 50,
        width: 50,
        height: 46,
    };

    return (
        <Stack direction="column" sx={{ width: '100%', gap: 0 }}>
            <AcknowledgedMessageStrip
                leadingContent={
                    showPaceDisplay ? (
                        <Tooltip
                            title={paceDisplay.hasPace ? 'Pace of play' : 'Pace clock not yet started'}
                            placement="bottom-start"
                        >
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.5 }}>
                                <TimerOutlinedIcon
                                    sx={{
                                        fontSize: 14,
                                        flexShrink: 0,
                                        color: paceDisplay.hasPace
                                            ? (paceDisplay.isOOP ? 'error.main' : 'primary.main')
                                            : 'text.disabled',
                                    }}
                                />
                                {paceDisplay.hasPace && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color: paceDisplay.isOOP ? 'error.main' : 'primary.main',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1,
                                        }}
                                    >
                                        {paceDisplay.elapsedFormatted}
                                        {paceDisplay.projectedFormatted !== '—' && (
                                            <Box component="span" sx={{ fontWeight: 400, color: paceDisplay.isOOP ? 'error.light' : 'text.secondary' }}>
                                                {' → '}{paceDisplay.projectedFormatted}
                                            </Box>
                                        )}
                                    </Typography>
                                )}
                            </Stack>
                        </Tooltip>
                    ) : undefined
                }
            />
            <Stack
                direction="row"
                sx={{
                    padding: scoreData.length > 0 ? '0 8px' : 0,
                    width: `calc(100% - 16px)`,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 0,
                    marginX: 'auto',
                }}
            >
            {roundFollowUp && (
                <RoundCompleteFollowUp
                    open={roundFollowUp}
                    setOpen={setRoundFollowUp}
                    newHoleSettings={newHoleSettings}
                />
            )}
            <EndOfRoundPopup
                open={endOfRoundOpen}
                onClose={() => setEndOfRoundOpen(false)}
                initialPhase={endOfRoundPhase}
                scoreCheck={scoreCheck}
                setScoreCheck={setScoreCheck}
                newHoleSettings={newHoleSettings}
                setRoundFollowUp={setRoundFollowUp}
            />
            <ChallengeSubmissionPrompt
                open={challengePromptOpen}
                challengeName={pendingChallengeName}
                onYes={handleChallengePromptYes}
                onNo={handleChallengePromptNo}
            />
            <ChallengeAcknowledgePrompt
                open={challengeAckOpen && !challengePromptOpen}
                challengeName={ackChallengeName}
                onAcknowledge={handleChallengeAcknowledge}
            />
            {scoreData.length > 0 && (
                <>
                    <IconButton
                        disabled={startHole === holeSettings.hole && !roundReview}
                        sx={navIconSx}
                        onClick={() => {
                            if (roundReview) {
                                setRoundReview(false);
                            }
                            void handleHole(-1);
                        }}
                    >
                        <ArrowLeftOutlined
                            sx={{
                                fontSize: 24,
                                color:
                                    startHole === holeSettings.hole && !roundReview
                                        ? 'darkgrey'
                                        : 'secondary.main',
                            }}
                        />
                    </IconButton>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        flex={1}
                        minWidth={0}
                        sx={{ px: 0.5 }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={1.25}
                            sx={{
                                minWidth: 0,
                                maxWidth: '100%',
                            }}
                        >
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                border: 2,
                                borderColor: 'primary.main',
                                backgroundColor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontWeight: 500,
                                    color: 'primary.dark',
                                    lineHeight: 1,
                                    fontSize: '1.5rem',
                                }}
                            >
                                {holeSettings.hole || '-'}
                            </Typography>
                        </Box>

                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={0.35}
                            minWidth={0}
                            sx={{
                                flex: '1 1 auto',
                                maxWidth: { xs: 'min(240px, calc(100vw - 220px))', sm: 'min(280px, 50vw)' },
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{
                                    flexWrap: 'nowrap',
                                    minWidth: 0,
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: 'primary.dark',
                                        fontWeight: 500,
                                        fontSize: '1.0825rem',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        flex: '1 1 auto',
                                        flexShrink: 0,
                                        textAlign: 'left',
                                    }}
                                >
                                    Par {holeSettings.par ?? '-'}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'primary.light',
                                        fontWeight: 400,
                                        fontSize: '1rem',
                                        lineHeight: 1,
                                        flexShrink: 0,
                                        px: 0.125,
                                        textAlign: 'center',
                                    }}
                                >
                                    |
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'primary.dark',
                                        fontWeight: 500,
                                        fontSize: '1.0825rem',
                                        lineHeight: 1.2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                        flex: '1 1 auto',
                                        textAlign: 'center',
                                    }}
                                >
                                    {holeSettings.dist ?? '-'}
                                </Typography>
                            </Stack>
                            {currentRound > 0 && !!eventData?.rounds?.[currentRound - 1]?.playFormat && (
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: 'primary.dark',
                                        fontWeight: 500,
                                        fontSize: '0.9rem',
                                        lineHeight: 1.3,
                                        width: '100%',
                                        textAlign: 'center',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    Format: {eventData.rounds[currentRound - 1]?.playFormat ?? ''}
                                </Typography>
                            )}
                        </Stack>
                        </Stack>
                    </Stack>

                    <IconButton
                        disabled={forwardDisabled}
                        sx={navIconSx}
                        onClick={() => {
                            if (roundComplete && isFinishForward) {
                                void handleFinish();
                            } else {
                                void handleHole(1);
                            }
                        }}
                    >
                        {isFinishForward ? (
                            <Check sx={{ fontSize: 28, color: 'secondary.main' }} />
                        ) : (
                            <ArrowRightOutlined sx={{ fontSize: 24, color: 'secondary.main' }} />
                        )}
                    </IconButton>
                </>
            )}
            </Stack>
        </Stack>
    );
}
